import { Button } from "@/components/ui/button";
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator,
	ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { LayoutMenu } from "@/components/workspace/LayoutMenu";
import { cn } from "@/lib/utils";
import type { GlobalTab } from "@/lib/tabs";
import { getModuleDefs } from "@/lib/workspaces";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface TabBarProps {
	tabs: GlobalTab[];
	activeTabId: string;
	onTabChange: (tabId: string) => void;
	onTabClose: (tabId: string) => void;
	onCloseOthers: (tabId: string) => void;
	onCloseRight: (tabId: string) => void;
	onReorder: (fromIndex: number, toIndex: number) => void;
	onTogglePin: (tabId: string) => void;
}

const moduleIconMap = new Map<string, LucideIcon>();
function getModuleIcon(moduleId?: string): LucideIcon | undefined {
	if (!moduleId) return undefined;
	if (moduleIconMap.size === 0) {
		for (const m of getModuleDefs()) {
			moduleIconMap.set(m.id, m.icon);
		}
	}
	return moduleIconMap.get(moduleId);
}

export function TabBar({
	tabs,
	activeTabId,
	onTabChange,
	onTabClose,
	onCloseOthers,
	onCloseRight,
	onReorder,
	onTogglePin,
}: TabBarProps) {
	const { t } = useTranslation();
	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

	const checkOverflow = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		checkOverflow();
		const el = scrollRef.current;
		if (!el) return;
		el.addEventListener("scroll", checkOverflow);
		const ro = new ResizeObserver(checkOverflow);
		ro.observe(el);
		return () => {
			el.removeEventListener("scroll", checkOverflow);
			ro.disconnect();
		};
	}, [checkOverflow, tabs.length]);

	const scroll = useCallback((dir: "left" | "right") => {
		scrollRef.current?.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });
	}, []);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent, tabId: string) => {
			if (e.button === 1) {
				e.preventDefault();
				const tab = tabs.find((t) => t.id === tabId);
				if (tab && !tab.pinned) onTabClose(tabId);
			}
		},
		[tabs, onTabClose],
	);

	// Drag and drop
	const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", String(index));
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		setDragOverIndex(index);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent, toIndex: number) => {
			e.preventDefault();
			const fromIndex = Number(e.dataTransfer.getData("text/plain"));
			if (!Number.isNaN(fromIndex) && fromIndex !== toIndex) {
				onReorder(fromIndex, toIndex);
			}
			setDragOverIndex(null);
		},
		[onReorder],
	);

	const handleDragEnd = useCallback(() => setDragOverIndex(null), []);

	// Detect module boundaries for visual separators
	const moduleBreaks = useMemo(() => {
		const breaks = new Set<number>();
		for (let i = 1; i < tabs.length; i++) {
			if (tabs[i].moduleId && tabs[i].moduleId !== tabs[i - 1].moduleId) {
				breaks.add(i);
			}
		}
		return breaks;
	}, [tabs]);

	if (tabs.length === 0) return null;

	return (
		<div className="flex items-stretch h-6 shrink-0 bg-muted/40 border-t select-none">
			{canScrollLeft && (
				<Button
					variant="ghost"
					size="icon"
					className="w-5 h-full rounded-none shrink-0 text-muted-foreground"
					onClick={() => scroll("left")}
					aria-label={t("scroll_tabs_left")}
				>
					<ChevronLeft className="h-3 w-3" />
				</Button>
			)}

			<div
				ref={scrollRef}
				className="flex items-stretch flex-1 overflow-x-auto scrollbar-none"
				style={{ scrollbarWidth: "none" }}
			>
				{tabs.map((tab, i) => {
					const isActive = tab.id === activeTabId;
					const Icon = getModuleIcon(tab.moduleId);
					const isModuleBreak = moduleBreaks.has(i);
					const isDragTarget = dragOverIndex === i;

					return (
						<ContextMenu key={tab.id}>
							<ContextMenuTrigger asChild>
								<div
									className={cn(
										"flex items-stretch shrink-0",
										isDragTarget && "border-l-2 border-l-primary",
									)}
									draggable={!tab.pinned}
									onDragStart={(e) => handleDragStart(e, i)}
									onDragOver={(e) => handleDragOver(e, i)}
									onDrop={(e) => handleDrop(e, i)}
									onDragEnd={handleDragEnd}
								>
									{/* Module group separator */}
									{isModuleBreak && (
										<div className="w-px self-center h-3.5 bg-border mx-0.5" />
									)}
									{/* Tab separator */}
									{i > 0 && !isModuleBreak && (
										<div className="w-px self-center h-3 bg-border/50" />
									)}

									<div
										role="tab"
										tabIndex={0}
										aria-selected={isActive}
										className={cn(
											"flex items-center gap-1 px-2 h-full text-[11px] whitespace-nowrap",
											"border-t-2 border-t-transparent -mt-px",
											"hover:bg-accent/50 group relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
											"max-w-[180px] min-w-[60px]",
											isActive
												? "bg-background border-t-primary text-foreground"
												: "text-muted-foreground",
										)}
										onClick={() => onTabChange(tab.id)}
										onMouseDown={(e) => handleMouseDown(e, tab.id)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												onTabChange(tab.id);
											}
										}}
									>
										{tab.dirty && (
											<span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
										)}
										{Icon && <Icon className="h-3 w-3 shrink-0 opacity-50" />}
										<span className="truncate">{tab.title}</span>
										{!tab.pinned && (
											<Button
												variant="ghost"
												size="icon"
												className={cn(
													"w-4 h-4 ml-auto",
													"opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
													isActive && "opacity-100",
												)}
												onClick={(e) => {
													e.stopPropagation();
													onTabClose(tab.id);
												}}
												aria-label={t("close_tab")}
											>
												<X className="h-2.5 w-2.5" />
											</Button>
										)}
									</div>
								</div>
							</ContextMenuTrigger>
							<ContextMenuContent>
								{!tab.pinned ? (
									<ContextMenuItem onSelect={() => onTogglePin(tab.id)}>
										{t("pin_tab")}
									</ContextMenuItem>
								) : (
									<ContextMenuItem onSelect={() => onTogglePin(tab.id)}>
										{t("unpin_tab")}
									</ContextMenuItem>
								)}
								<ContextMenuSeparator />
								<ContextMenuItem
									onSelect={() => onTabClose(tab.id)}
									disabled={tab.pinned}
								>
									{t("close_tab")}
								</ContextMenuItem>
								<ContextMenuItem onSelect={() => onCloseOthers(tab.id)}>
									{t("close_others")}
								</ContextMenuItem>
								<ContextMenuItem onSelect={() => onCloseRight(tab.id)}>
									{t("close_right")}
								</ContextMenuItem>
							</ContextMenuContent>
						</ContextMenu>
					);
				})}
			</div>

			{canScrollRight && (
				<Button
					variant="ghost"
					size="icon"
					className="w-5 h-full rounded-none shrink-0 text-muted-foreground"
					onClick={() => scroll("right")}
					aria-label={t("scroll_tabs_right")}
				>
					<ChevronRight className="h-3 w-3" />
				</Button>
			)}

			<div className="w-px self-center h-3.5 bg-border mx-0.5" />
			<LayoutMenu />
		</div>
	);
}
